import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiDownload, FiTrendingUp } from "react-icons/fi";
import salesService from "../../api/services/salesService";
import agentReportsService from "../../api/services/agentReportsService";
import { TableSkeleton } from "../../components/common/skeletons";

const THEME = { primary: "#FB565F" };

export default function SalesPerformancePanel() {
  const [period, setPeriod] = useState("daily");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await salesService.getTeamPerformance({ period });
      setData(res);
    } catch {
      toast.error("Failed to load sales performance");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    try {
      const blob = await agentReportsService.exportMonthlyRevenue({});
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sales-revenue.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const rows = data?.performance || [];
  const proofs = data?.paymentProofs || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Sales team performance — calls, revenue, and manual payment records
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setPeriod(period === "daily" ? "monthly" : "daily")}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100"
          >
            {period === "daily" ? "Show monthly" : "Show daily"}
          </button>
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
            style={{ backgroundColor: THEME.primary }}
          >
            <FiDownload /> Export revenue CSV
          </button>
        </div>
      </div>

      {loading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Sales agent</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-500">Users responded</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-500">Calls</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-500">Call min</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-500">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.agent_id || r.agentId} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <p className="font-medium">{r.name || `${r.firstName || ""} ${r.lastName || ""}`.trim()}</p>
                      <p className="text-xs text-gray-400">{r.email}</p>
                    </td>
                    <td className="px-3 py-2 text-center font-semibold">
                      {r.support_users_responded || 0}
                    </td>
                    <td className="px-3 py-2 text-center">{r.calls_count || 0}</td>
                    <td className="px-3 py-2 text-center">
                      {Math.round((r.calls_duration_sec || 0) / 60)}
                    </td>
                    <td className="px-3 py-2 text-center font-semibold text-emerald-700">
                      {r.sales_revenue || r.subscription_revenue || 0}
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={5} className="px-3 py-12 text-center text-gray-400">
                      No sales performance data for this period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FiTrendingUp style={{ color: THEME.primary }} />
              Manual payment screenshots
            </h3>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">Customer</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">Agent</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Amount</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Date</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-500">Proof</th>
                  </tr>
                </thead>
                <tbody>
                  {proofs.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        <p className="font-medium">
                          {p.firstName} {p.lastName}
                        </p>
                        <p className="text-xs text-gray-400">{p.email}</p>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {p.agent_firstName
                          ? `${p.agent_firstName} ${p.agent_lastName}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.amount} {p.currency || ""}
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-gray-500">
                        {p.start_date ? new Date(p.start_date).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.payment_proof_url ? (
                          <a
                            href={p.payment_proof_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-red-600 font-semibold text-xs hover:underline"
                          >
                            View
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                  {!proofs.length && (
                    <tr>
                      <td colSpan={5} className="px-3 py-12 text-center text-gray-400">
                        No manual payment proofs for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
